import React, { useState, useRef, useEffect } from 'react';
import * as turf from '@turf/turf';
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  useMapEvents,
  useMap,
  Polygon,
  Marker,
  Popup
} from 'react-leaflet';
import { useSelector, useDispatch } from 'react-redux';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import { useQuery } from '@apollo/client';
import { GET_LAND_PARCELS } from 'graphql/gis';
import { EditControl } from 'react-leaflet-draw';
import SearchControl from './SearchControl';
import L from 'leaflet';
import PropTypes from 'prop-types';
import { calculatePolygonAreaHa } from 'helpers/gis';
import {
  addDrawnLandParcel,
  toggleSelectLandParcel,
  setAllMarkerPoints,
  setPlantingDataSource,
  setZoom
} from 'reducers/landDevReducer';
import ReactDOMServer from 'react-dom/server';
import { ReactComponent as OakSvg } from '../../../../../assets/icons/oak.svg';
import { ReactComponent as PoplarSvg } from '../../../../../assets/icons/poplar.svg';
import { ReactComponent as ChestnutSvg } from '../../../../../assets/icons/chestnut.svg';
import { ReactComponent as SolarSvg } from '../../../../../assets/icons/solar.svg';
import { ReactComponent as WindSvg } from '../../../../../assets/icons/wind.svg';
import { Button, Modal, Form } from 'react-bootstrap';

const MapEvents = ({ onZoomLevelChange, onMapMoveEnd }) => {
  const map = useMapEvents({
    zoom: () => {
      const newZoomLevel = map.getZoom();
      onZoomLevelChange(newZoomLevel);
    },
    moveend: () => {
      onMapMoveEnd(map.getBounds());
    }
  });

  return null;
};

MapEvents.propTypes = {
  onZoomLevelChange: PropTypes.func,
  onMapMoveEnd: PropTypes.func
};

const MapUpdateLocation = ({ centerCoor }) => {
  const map = useMap();
  useEffect(() => {
    console.log('centerCoor ', centerCoor);
    map.setView(centerCoor, map.getZoom());
  }, [centerCoor, map]);

  return null;
};

MapUpdateLocation.propTypes = {
  centerCoor: PropTypes.array.isRequired
};

const LandDevMap = ({ currentState, onEditMounted }) => {
  const dispatch = useDispatch();

  const map = useRef();
  const [topLeft, setTopLeft] = useState(null);
  const [bottomRight, setBottomRight] = useState(null);
  const {
    centerCoor,
    selectedLandParcels,
    allMarkerPoints,
    itemType,
    plantingDataSource,
    zoom
  } = useSelector(state => state.landDev);

  const [landParcels, setLandParcels] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [itemNo, setItemNo] = useState(10);
  const [layer, setLayer] = useState(null);
  const [plantArea, setPlantArea] = useState(null);
  const [maxTrees, setMaxTrees] = useState(15);
  const [distanceApart, setDistanceApart] = useState(30);
  const [intersectCoords, setintersectCoords] = useState(null);

  const handleZoomLevelChange = newZoomLevel => {
    dispatch(setZoom(newZoomLevel));
  };
  const handleMapMoveEnd = bounds => {
    setTopLeft(bounds.getNorthWest());
    setBottomRight(bounds.getSouthEast());
  };

  let { data } = useQuery(GET_LAND_PARCELS, {
    variables: {
      topLeftLat: topLeft?.lat,
      topLeftLng: topLeft?.lng,
      btmRightLat: bottomRight?.lat,
      btmRightLng: bottomRight?.lng
    },
    skip: !topLeft || !bottomRight || zoom < 16
  });

  const handlePolygonCreate = ({ layer }) => {
    setLayer(layer);
    let coordinates = layer.getLatLngs()[0].map(p => [p.lat, p.lng]);
    coordinates = [...coordinates, coordinates[0]];

    if (currentState === 1) {
      const bounds = layer.getBounds();
      const xMin = bounds.getEast();
      const xMax = bounds.getWest();
      const yMin = bounds.getSouth();
      const yMax = bounds.getNorth();
      let vertices = null;

      if (layer.getLatLngs()[0]) {
        const outerRing = layer.getLatLngs()[0];
        vertices = outerRing.map(coordinate => ({
          lat: coordinate.lat,
          lng: coordinate.lng,
          key: Date.now()
        }));
      }

      const landParcel = {
        id: Date.now(),
        parcelId: Date.now(),
        coordinates: coordinates,
        area: calculatePolygonAreaHa(coordinates),
        isDrawn: true,
        vertices,
        xMin,
        xMax,
        yMin,
        yMax
      };

      dispatch(addDrawnLandParcel(landParcel));

      layer.remove();
    } else {
      // currentState is 2
      let parentCoordinates = selectedLandParcels[0].coordinates;
      let intersection = turf.intersect(
        turf.featureCollection([
          turf.polygon([coordinates]),
          turf.polygon([parentCoordinates])
        ])
      );

      if (!intersection) {
        layer.remove();
        handleOpenErrorModal();
      } else {
        setintersectCoords(intersection.geometry.coordinates[0]);
        setPlantArea(
          calculatePolygonAreaHa(intersection.geometry.coordinates[0])
        );
        handleOpenModal();
        // eventually handled by handleSaveChanges();
      }
    }
  };

  // for displaying land boundaries provided by geoserver
  useEffect(() => {
    if (!data) {
      return;
    }

    const landParcels = data.landParcels.items.map(p => ({
      id: p.id,
      coordinates: p.polygon.points.map(p => [p.lat, p.lng]),
      attributes: p.attributes,
      area: p.area
    }));

    setLandParcels(landParcels);
  }, [data, selectedLandParcels, itemType, zoom]);

  useEffect(() => {
    calculateMaxTrees();
  }, [plantArea]);

  const handleCloseModal = () => {
    setLayer(null);
    setDistanceApart(30);
    setMaxTrees(15);
    setShowModal(false);
    setShowErrorModal(false);
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleOpenErrorModal = () => {
    setShowErrorModal(true);
  };

  const handleDistanceChange = distance => {
    setDistanceApart(distance);
    calculateMaxTrees();
  };

  const handleSaveChanges = () => {
    handleCloseModal();

    const activePolygon = L.polygon(intersectCoords);
    const bounds = activePolygon.getBounds();
    const x_min = bounds.getEast();
    const x_max = bounds.getWest();
    const y_min = bounds.getSouth();
    const y_max = bounds.getNorth();

    const dx = (x_max - x_min) / Math.sqrt(itemNo);
    const dy = (y_max - y_min) / Math.sqrt(itemNo);

    let pointCount = 0;

    for (let i = 0; i < Math.sqrt(itemNo); i++) {
      for (let j = 0; j < Math.sqrt(itemNo); j++) {
        const lat = y_min + dy * i + dy / 2;
        const lng = x_min + dx * j + dx / 2;
        const turfPoint = turf.point([lng, lat]);
        const poly = activePolygon.toGeoJSON();
        const turfInside = turf.booleanPointInPolygon(turfPoint, poly);
        const timestamp = Date.now();

        if (turfInside) {
          const points = {
            id: `${pointCount}_${itemType}_${timestamp}`,
            lat: lat,
            lng: lng,
            itemType: itemType
          };
          dispatch(setAllMarkerPoints(points));
          pointCount++;
        }
      }
    }

    // we need to save the polygon inside here as well.
    const plantingDataSource = {
      id: Date.now(), // once the id is unique, the table removes accordingly, but not the map.
      parcel: Date.now(),
      name: getItemName(itemType),
      numberOfItems: itemNo,
      itemType: itemType,
      coordinates: intersectCoords
    };

    dispatch(setPlantingDataSource(plantingDataSource));

    layer.remove();
  };

  const calculateMaxTrees = () => {
    setMaxTrees(
      Math.floor(
        (plantArea * 10000) / (Math.pow(distanceApart / 3.281, 2) * 3.142)
      )
    );
  };

  const getIcon = itemType => {
    switch (itemType) {
      case 1:
        return new L.DivIcon({
          html: ReactDOMServer.renderToString(
            <OakSvg className="land-dev-tree-icon" />
          ),
          className: 'oak'
        });
      case 2:
        return new L.DivIcon({
          html: ReactDOMServer.renderToString(
            <PoplarSvg className="land-dev-tree-icon" />
          ),
          className: 'poplar'
        });
      case 3:
        return new L.DivIcon({
          html: ReactDOMServer.renderToString(
            <ChestnutSvg className="land-dev-tree-icon" />
          ),
          className: 'chestnut'
        });
      case 4:
        return new L.DivIcon({
          html: ReactDOMServer.renderToString(
            <SolarSvg className="land-dev-tree-icon" />
          ),
          className: 'solar'
        });

      default:
        return new L.DivIcon({
          html: ReactDOMServer.renderToString(
            <WindSvg className="land-dev-tree-icon" />
          ),
          className: 'wind'
        });
    }
  };

  const getItemName = itemType => {
    switch (itemType) {
      case 1:
        return 'Oak';
      case 2:
        return 'Yellow Poplar';
      case 3:
        return 'Horse Chestnut';
      case 4:
        return 'Solar Panel';
      default:
        return 'Wind Turbine';
    }
  };

  const selectLandParcel = parcel => {
    const parcelId = Date.now();
    const coordinates = parcel.coordinates.map(p => ({
      lat: p[0],
      lon: p[1]
    }));
    const polygon = L.polygon(coordinates);
    const bounds = polygon.getBounds();
    const xMin = bounds.getEast();
    const xMax = bounds.getWest();
    const yMin = bounds.getSouth();
    const yMax = bounds.getNorth();
    let vertices = null;

    if (polygon.getLatLngs()[0]) {
      const outerRing = polygon.getLatLngs()[0];
      vertices = outerRing.map((coordinate, index) => ({
        lat: coordinate.lat,
        lng: coordinate.lng,
        key: `${parcel.id}-${index}`
      }));
    }

    const parcels = {
      ...parcel,
      vertices,
      xMin,
      xMax,
      yMin,
      yMax,
      parcelId: parcelId
    };
    dispatch(toggleSelectLandParcel(parcels));
  };

  return (
    <>
      <MapContainer className="h-100" center={centerCoor} zoom={zoom} ref={map}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {currentState === 1 && (
          <>
            <SearchControl
              provider={new OpenStreetMapProvider()}
              showMarker={true}
              showPopup={false}
              popupFormat={({ result }) => result.label}
              maxMarkers={3}
              retainZoomLevel={false}
              animateZoom={true}
              autoClose={false}
              searchLabel={'Enter address, please'}
              keepResult={true}
            />
            <FeatureGroup>
              <EditControl
                position="topright"
                edit={{
                  edit: true,
                  poly: true
                }}
                onCreated={handlePolygonCreate}
                draw={{
                  rectangle: true,
                  polygon: true,
                  circle: false,
                  marker: false,
                  circlemarker: false,
                  polyline: false
                }}
              ></EditControl>
            </FeatureGroup>
          </>
        )}
        {currentState === 2 && (
          <>
            <FeatureGroup>
              <EditControl
                onMounted={onEditMounted}
                position="topright"
                edit={{
                  edit: false,
                  poly: true
                }}
                onCreated={handlePolygonCreate}
                draw={{
                  rectangle: false,
                  polygon: true,
                  circle: false,
                  marker: false,
                  circlemarker: false,
                  polyline: false
                }}
              ></EditControl>
            </FeatureGroup>
          </>
        )}

        {plantingDataSource &&
          plantingDataSource.map(parcel => {
            console.log('plantingDataSource', plantingDataSource);
            return (
              <Polygon
                key={parcel.id}
                pathOptions={{ color: 'green' }}
                positions={parcel.coordinates}
                eventHandlers={{
                  mouseover: e => e.target.setStyle({ color: 'white' }),
                  mouseout: e => e.target.setStyle({ color: 'green' })
                }}
                weight={1}
              ></Polygon>
            );
          })}

        {selectedLandParcels &&
          selectedLandParcels.map(parcel => {
            return (
              <Polygon
                key={parcel.id}
                pathOptions={{ color: 'blue' }}
                positions={parcel.coordinates}
                eventHandlers={
                  currentState === 1
                    ? {
                        click: () => selectLandParcel(parcel),
                        mouseover: e => e.target.setStyle({ color: 'green' }),
                        mouseout: e => e.target.setStyle({ color: 'blue' })
                      }
                    : null
                }
                weight={1}
              ></Polygon>
            );
          })}
        {currentState === 1 &&
          landParcels &&
          landParcels
            .filter(
              parcel => !selectedLandParcels.some(p => p.id === parcel.id)
            )
            .map(parcel => {
              return (
                <Polygon
                  key={parcel.id}
                  pathOptions={{ color: 'white' }}
                  positions={parcel.coordinates}
                  eventHandlers={{
                    click: () => selectLandParcel(parcel),
                    mouseover: e => e.target.setStyle({ color: 'green' }),
                    mouseout: e => e.target.setStyle({ color: 'white' })
                  }}
                  weight={1}
                >
                  {/* Tooltip content */}
                </Polygon>
              );
            })}
        {allMarkerPoints.length !== 0 &&
          allMarkerPoints.map(marker => (
            <Marker
              key={marker.id}
              position={[marker.lat, marker.lng]}
              icon={getIcon(marker.itemType)}
            >
              <Popup>Hello</Popup>
            </Marker>
          ))}

        <MapEvents
          onZoomLevelChange={handleZoomLevelChange}
          onMapMoveEnd={handleMapMoveEnd}
        />
        <MapUpdateLocation centerCoor={centerCoor} />
      </MapContainer>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header>
          <Modal.Title>Options</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Distance apart for each tree (in feet):</Form.Label>
            <Form.Control
              min="10"
              max="80"
              type="number"
              value={distanceApart}
              onChange={e => handleDistanceChange(e.target.value)}
            />
            <Form.Label>Number Of Items (Max: {maxTrees})</Form.Label>
            <Form.Control
              min="5"
              max={maxTrees}
              type="number"
              value={itemNo}
              onChange={e => setItemNo(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveChanges}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showErrorModal} onHide={handleCloseModal} centered>
        <Modal.Header>
          <Modal.Title>Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Invalid land selected. Please select an area within the bounds.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseModal}>
            Ok
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

LandDevMap.propTypes = {
  currentState: PropTypes.number.isRequired,
  onEditMounted: PropTypes.func
};

export default LandDevMap;
